import { getSelectionQueryParams } from '@/features/partner/redux/partner.actions';
import { GRAPHIC_TYPES_VALUES } from '@/constants/customization.constants';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '@/store';

export const useFilterItemUrl = () => {
  const { refData: _refData, activeAsset, sidebarFilters } = useSelector((state: AppState) => state.partner);

  const refData = useMemo(
    () => ({
      categories: [],
      types: [],
      sizes: [],
      colors: [],
      ..._refData,
    }),
    [_refData]
  );

  const filtersFromQueryParams = useMemo(() => {
    const newFiltersObj: any = {
      category: null,
      types: [],
      sizes: [],
      colors: [],
      graphicTypes: [], // color, engraved
      sortOrder: 'most-popular',
      search: '',
      isActive: false,
    };

    const queryParams: any = Object.fromEntries(new URLSearchParams(location.search).entries());

    let categoryExists = null;
    if (queryParams.category) {
      categoryExists = (refData.categories || []).find((c) => c.name === queryParams.category);

      if (categoryExists) newFiltersObj.category = categoryExists.id;
    }

    //* category id 9 === drinkwares
    if (queryParams.types && categoryExists?.id === 9) {
      queryParams.types = queryParams.types.split(',');

      for (const type of queryParams.types) {
        const typeExists = refData.types.find((s) => s.value === type);

        if (typeExists && newFiltersObj.types.indexOf(typeExists.id) < 0)
          newFiltersObj.types.push(typeExists.id);
      }
    }

    if (queryParams.sizes) {
      queryParams.sizes = queryParams.sizes.split(',');

      for (const size of queryParams.sizes) {
        const sizeExists = refData.sizes.find((s) => s.value === size);

        if (sizeExists && newFiltersObj.sizes.indexOf(sizeExists.id) < 0)
          newFiltersObj.sizes.push(sizeExists.id);
      }
    }

    if (queryParams.colors) {
      queryParams.colors = queryParams.colors.split(',');

      for (const color of queryParams.colors) {
        const colorExists = refData.colors.find((c) => c.value === color);

        if (colorExists && newFiltersObj.colors.indexOf(colorExists.id) < 0)
          newFiltersObj.colors.push(colorExists.id);
      }
    }

    if (queryParams.graphicTypes) {
      if (queryParams.graphicTypes) queryParams.graphicTypes = queryParams.graphicTypes.split(',');

      for (const graphicType of queryParams.graphicTypes) {
        if (
          GRAPHIC_TYPES_VALUES.findIndex((i) => i.value === graphicType) >= 0 &&
          newFiltersObj.graphicTypes.indexOf(graphicType) < 0
        )
          newFiltersObj.graphicTypes.push(graphicType);
      }
    }

    if (queryParams.sortOrder) newFiltersObj.sortOrder = queryParams.sortOrder;

    return newFiltersObj;
  }, [refData, activeAsset, sidebarFilters]);

  const getUrl = useCallback(
    (operation: 'add' | 'remove', key: string, val: any) => {
      if (!val) return '#';

      let newFiltersObj = { ...filtersFromQueryParams };
      if (operation === 'add') {
        if (key === 'category') {
          newFiltersObj = {
            ...newFiltersObj,
            colors: [],
            sizes: [],
            types: [],
            graphicTypes: [],
            category: val,
          };
        } else if (key === 'colors' || key === 'sizes' || key === 'graphicTypes' || key === 'types') {
          newFiltersObj = {
            ...newFiltersObj,
            [key]: [...newFiltersObj[key], val],
          };
          newFiltersObj[key] = [...new Set(newFiltersObj[key])];
        } else if (key === 'sortOrder' || key === 'search') {
          newFiltersObj = {
            ...newFiltersObj,
            [key]: val,
          };
        } else {
          console.error('Unknown filter key');
          return '#';
        }
      } else if (operation === 'remove') {
        if (key === 'category') {
          newFiltersObj = {
            ...newFiltersObj,
            colors: [],
            sizes: [],
            types: [],
            category: null,
          };
        } else if (key === 'colors' || key === 'sizes' || key === 'graphicTypes' || key === 'types') {
          const valIndex = newFiltersObj[key].indexOf(val);
          if (valIndex >= 0) {
            newFiltersObj = {
              ...newFiltersObj,
              [key]: newFiltersObj[key].filter((i: any) => i !== val),
            };
          }
        } else {
          console.error('Unknown filter key');
          return '#';
        }
      } else {
        return '#';
      }

      const newUrl =
        window.location.pathname +
        getSelectionQueryParams({ filterOptions: newFiltersObj, refData, asset: activeAsset });
      return newUrl;
    },
    [filtersFromQueryParams, refData]
  );

  return { getUrl };
};
